import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { setSupplierOrderRef } from "../api";
import type { Order } from "../types";

interface SupplierOrderRefEditorProps {
  order: Order;
  onSaved?: () => void;
}

/** Mini-editor inline del N.Ord.Forn (supplier_order_ref). Trigger discreto
 *  accanto al FornitoreBadge; al click mostra input + Salva. Niente popover
 *  (non c'e' nel progetto) -> niente dipendenza Radix, coerente con
 *  ShippingEditor. A salvataggio riuscito chiama onSaved (refresh lista): il
 *  badge mostra il numero perche' supplierBadgeName da' priorita' alla ref. */
export function SupplierOrderRefEditor({ order, onSaved }: SupplierOrderRefEditorProps) {
  const hasRef = Boolean(order.supplierOrderRef);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(order.supplierOrderRef ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function open() {
    setValue(order.supplierOrderRef ?? "");
    setErr(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setErr(null);
  }

  async function save() {
    const v = value.trim();
    if (!v) {
      setErr("Inserisci il N.Ord.Forn.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await setSupplierOrderRef(order.id, v);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setErr(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        data-no-open
        onClick={open}
        title={hasRef ? "Modifica N.Ord.Forn" : "Inserisci N.Ord.Forn a mano"}
        aria-label="Numero ordine fornitore"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] text-ink-3 hover:text-accent hover:bg-accent-soft transition-colors"
      >
        <svg
          viewBox="0 0 24 24"
          className="w-[11px] h-[11px] fill-none stroke-current stroke-[2] [stroke-linecap:round] [stroke-linejoin:round]"
        >
          {hasRef ? (
            <path d="M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
          ) : (
            <path d="M12 5v14 M5 12h14" />
          )}
        </svg>
        {hasRef ? "Modifica" : "N.Ord.Forn"}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" data-no-open>
      <input
        autoFocus
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
          if (e.key === "Escape") cancel();
        }}
        placeholder="N.Ord.Forn"
        aria-label="Numero ordine fornitore"
        className="w-[120px] px-2 py-0.5 bg-card border border-line rounded-sm text-[12px] font-mono outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        className="px-2 py-0.5 rounded-sm text-[11px] font-medium bg-accent-2 text-white border border-accent hover:brightness-95 disabled:opacity-50 transition-colors"
      >
        {busy ? "Salvo…" : "Salva"}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        aria-label="Annulla"
        className="px-1.5 py-0.5 rounded-sm text-[11px] text-ink-3 hover:text-ink transition-colors"
      >
        Annulla
      </button>
      {err && <span className="text-[11px] text-red">{err}</span>}
    </span>
  );
}
